/**
 * Dashboard Content Component
 *
 * Renders different content based on the current view state
 * Integrates various modules into the main dashboard
 */

import React from 'react';
import {useDashboardViewStore} from '../store/dashboardViewStore';
import {useDashboardManagement} from '../hooks/useDashboardManagement';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {UserRole} from '../../../constants/roles';
import {AppCardTitle} from '../../../Presentation/components/ui/Typography';

// Import different view components
import {WelcomeBanner} from './WelcomeBanner';
import {ProviderInfoBanner} from './ProviderInfoBanner';
import {AccountingUserInfo} from './AccountingUserInfo';
import {UsageInstructions} from './UsageInstructions';
import {HomeAnalytics} from '../analytics';
import {StoreMapRefactored} from '../../../Presentation/components/common/StoreMapRefactored';

import {
  AppTitle,
  AppText
} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';

const DashboardContent: React.FC = () => {
  const {currentView} = useDashboardViewStore();
  const {hasRole} = useAuthorization();
  const {user, isProvider, brandNames} = useDashboardManagement();

  // User role checks
  const isAccountingUser =
    hasRole(UserRole.ACCOUNTANT) || hasRole(UserRole.ACCOUNTING_MANAGER);

  const renderHomeView = () => (
    <div className="space-y-6">
      {isProvider && (
        <ProviderInfoBanner username={user?.username} brandNames={brandNames} />
      )}
      <WelcomeBanner />
      {isAccountingUser && <AccountingUserInfo />}
      <HomeAnalytics />
      <UsageInstructions />
    </div>
  );

  const renderMapView = () => (
    <div className="space-y-6">
      <div>
        <AppTitle className="text-3xl font-bold text-gray-900 mb-2">
          الخريطة
        </AppTitle>
        <AppText className="text-gray-600">
          عرض المتاجر والمسوقين على الخريطة
        </AppText>
      </div>
      <div className="h-[600px]">
        <StoreMapRefactored viewMode="stores" />
      </div>
    </div>
  );

  const renderAnalyticsView = () => (
    <div className="space-y-6">
      <div>
        <AppTitle className="text-3xl font-bold text-gray-900 mb-2">
          التحليلات
        </AppTitle>
        <AppText className="text-gray-600">
          تحليلات مفصلة للبيانات والإحصائيات
        </AppText>
      </div>
      <Card className="p-12 text-center">
        <div className="text-gray-400 mb-4">
          <svg
            className="mx-auto h-16 w-16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        <AppCardTitle className="font-semibold text-gray-500 mb-2">
          قريباً - التحليلات المتقدمة
        </AppCardTitle>
        <AppText className="text-gray-400">
          سيتم إضافة التحليلات المتقدمة والرسوم البيانية التفصيلية قريباً
        </AppText>
      </Card>
    </div>
  );

  switch (currentView) {
    case 'map':
      return renderMapView();
    case 'analytics':
      return renderAnalyticsView();
    case 'home':
    default:
      return renderHomeView();
  }
};

export default DashboardContent;
